// ─── apps/tenant/src/lib/supabase.js ────────────────────────────────────────
// Rewritten 21 Sep 2026 (step 5 lockdown).
//
// WHAT CHANGED: the browser no longer holds ANY data-plane credentials. The
// old file created a data-plane client with VITE_SHARED_SUPABASE_SERVICE_KEY
// — a service-role key baked into the public bundle, readable by anyone who
// opened the app, bypassing RLS for every tenant. Every read AND write now
// goes through /api/data, which validates the caller's master-project JWT and
// force-injects the caller's account_id server-side. Storage access uses
// short-lived signed URLs minted by the same proxy.
//
// The public API of this module (every export and method signature) is
// unchanged, so pages and components need no edits — with one exception:
// getTenantClient() now returns null (see its comment), and the two
// components that used it for storage now use the `storage` export.
//
// After this ships and is verified, VITE_SHARED_SUPABASE_URL /
// _ANON_KEY / _SERVICE_KEY can be deleted from the Vercel env and the
// data-plane service key rotated. See supabase/RUNBOOK-step5.md.

import { createClient } from "@supabase/supabase-js";

// ─── Master Supabase (platform control plane — login + account lookup) ──────
const masterUrl = import.meta.env.VITE_SUPABASE_URL;
const masterAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
export const masterSupabase = createClient(masterUrl, masterAnonKey, {
    auth: { storageKey: "sb-tenant-auth" },
});

let currentAccount = null;
let currentUserRole = "viewer";

export async function initializeTenantClient(session) {
    if (!session) return null;

    const res = await fetch(
        `${masterUrl}/rest/v1/account_users?select=*,accounts(id,name,slug,plan,status,ai_provider,ai_model,widget_color,widget_position,widget_greeting,widget_bot_name,widget_logo_url,custom_domain,conversation_limit,byok_enabled,org_context,org_industry,theme_primary_color,theme_secondary_color,theme_sidebar_bg,theme_sidebar_text,theme_main_bg,theme_card_bg,theme_card_border,theme_logo_url,theme_logo_width,theme_favicon_url,theme_font_family)&auth_user_id=eq.${session.user.id}&is_active=eq.true`,
        {
            headers: {
                "apikey": masterAnonKey,
                "Authorization": `Bearer ${session.access_token}`,
            },
        }
    );
    const rows = await res.json();

    const accountUser = rows?.[0];
    if (!accountUser?.accounts) return null;

    currentAccount = accountUser.accounts;
    currentUserRole = accountUser.role || "viewer";

    // NOTE (step 5): the old browser-side upsert into the data plane's
    // user_accounts table is gone — it required the service key. The proxy
    // derives the account from the master JWT on every request, so the data
    // plane no longer needs a browser-maintained user mapping at all.
    // (provision.js still syncs user_accounts server-side for RLS defense.)

    return currentAccount;
}

export function getAccount() { return currentAccount; }
export function getUserRole() { return currentUserRole; }
export function getAccountId() { return currentAccount?.id; }
export function getIsProvisioned() { return !!currentAccount; }

// DEPRECATED (step 5): there is no browser data-plane client anymore. Kept so
// legacy call sites fail soft (they all null-check). Storage needs are served
// by the `storage` export below.
export function getTenantClient() {
    console.warn("getTenantClient() is deprecated — the data plane is only reachable via /api/data.");
    return null;
}

export const auth = {
    signIn: (email, password) => masterSupabase.auth.signInWithPassword({ email, password }),
    signOut: () => masterSupabase.auth.signOut(),
    getSession: () => masterSupabase.auth.getSession(),
    onAuthStateChange: (cb) => masterSupabase.auth.onAuthStateChange(cb),
};

// ─── /api/data transport ────────────────────────────────────────────────────
// Every data-plane operation flows through here. The response shape mimics
// supabase-js ({ data, error }) so existing page code keeps working.
// Errors come back as objects with .message — the shape every page was
// written against in the safeQuery era. Returning bare strings here made
// `result.error.message` undefined and every failure silently invisible.
const asErr = (m) => ({ message: typeof m === "string" ? m : (m?.message || "Unknown error") });
async function callData(op) {
    try {
        const { data: { session } } = await masterSupabase.auth.getSession();
        if (!session?.access_token) return { data: null, error: asErr("Not authenticated") };
        const res = await fetch("/api/data", {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${session.access_token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(op),
        });
        const json = await res.json();
        if (!res.ok) return { data: null, error: asErr(json.error || `HTTP ${res.status}`) };
        return { data: json.data, error: null };
    } catch (err) {
        return { data: null, error: asErr(err) };
    }
}

const rpc = (name, args = {}) => callData({ op: "rpc", name, args });
const selectData = (table, opts = {}) => callData({ op: "select", table, ...opts });
const insertData = (table, values) => callData({ op: "insert", table, values });
const updateData = (table, opts) => callData({ op: "update", table, ...opts });
const upsertData = (table, values, onConflict) => callData({ op: "upsert", table, values, onConflict });
const deleteData = (table, opts) => callData({ op: "delete", table, ...opts });
const first = (r) => ({ data: Array.isArray(r.data) ? (r.data[0] ?? null) : r.data, error: r.error });
const stamp = (d) => ({ ...d, updated_at: new Date().toISOString() });

// ─── Storage (signed URLs via the proxy) ────────────────────────────────────
export const storage = {
    // Upload a File/ArrayBuffer to a path that upload-document already
    // registered on a documents row for this account.
    upload: async (path, fileOrBuffer, contentType = "application/pdf") => {
        const { data, error } = await callData({ op: "storage_sign", mode: "upload", path });
        if (error || !data?.signedUrl) return { error: error?.message || "Could not sign upload" };
        const res = await fetch(data.signedUrl, {
            method: "PUT",
            headers: { "Content-Type": contentType, "x-upsert": "true" },
            body: fileOrBuffer,
        });
        if (!res.ok) return { error: `Upload failed: HTTP ${res.status}` };
        return { error: null };
    },
    // Download a document's file as a Blob.
    download: async (path) => {
        const { data, error } = await callData({ op: "storage_sign", mode: "download", path });
        if (error || !data?.signedUrl) return { data: null, error: error?.message || "Could not sign download" };
        const res = await fetch(data.signedUrl);
        if (!res.ok) return { data: null, error: `Download failed: HTTP ${res.status}` };
        return { data: await res.blob(), error: null };
    },
};

// ─── Documents ──────────────────────────────────────────────────────────────
export const documents = {
    list: (params = {}) => selectData("documents", {
        filters: params.status ? { status: params.status } : {},
        order: { column: "created_at", ascending: false },
    }),
    get: (id) => selectData("documents", { filters: { id }, single: true }),
    create: (data) => first(insertData("documents", data)),
    delete: (id) => deleteData("documents", { id }),
};

// ─── Catalog ────────────────────────────────────────────────────────────────
export const catalog = {
    list: (params = {}) => selectData("catalog_items", {
        filters: params.category ? { category: params.category } : {},
        order: { column: "name" },
    }),
    create: (data) => first(insertData("catalog_items", data)),
    update: (id, data) => first(updateData("catalog_items", { id, values: data })),
};

// ─── Products ───────────────────────────────────────────────────────────────
export const products = {
    list: (params = {}) => {
        const filters = {};
        if (params.category) filters.category = params.category;
        if (params.active !== undefined) filters.is_active = params.active;
        if (params.featured !== undefined) filters.is_featured = params.featured;
        if (params.search) filters.$search = params.search;
        return selectData("products", { filters, order: [{ column: "sort_order" }, { column: "name" }] });
    },
    get: (id) => selectData("products", { filters: { id }, single: true }),
    create: (data) => first(insertData("products", data)),
    createMany: (rows) => insertData("products", rows),
    update: (id, data) => first(updateData("products", { id, values: stamp(data) })),
    delete: (id) => deleteData("products", { id }),
    deleteMany: (ids) => deleteData("products", { filters: { id: ids } }),
    stats: () => rpc("get_product_stats"),
    categories: () => selectData("products", {
        columns: "category", filters: { $notnull: "category" }, order: { column: "category" },
    }),
};

// Trigger embedding generation via the edge function (unchanged — no keys).
export async function embedProducts(opts = {}) {
    const account = getAccount();
    if (!account?.slug) return { error: "No account" };
    try {
        const res = await fetch(`${masterUrl}/functions/v1/embed-products?account=${account.slug}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ limit: opts.limit || 200, force: opts.force || false }),
        });
        return await res.json();
    } catch (err) {
        return { error: err.message };
    }
}

// ─── Chat Widget Config ─────────────────────────────────────────────────────
export const chatWidget = {
    get: (slug = "default") => selectData("chat_widget_configs", { filters: { slug }, single: true }),
    list: () => selectData("chat_widget_configs", { order: { column: "created_at" } }),
    create: (data) => first(insertData("chat_widget_configs", data)),
    update: (id, data) => first(updateData("chat_widget_configs", { id, values: stamp(data) })),
    upsertBySlug: (slug, data) => first(upsertData("chat_widget_configs", stamp({ ...data, slug }), "account_id,slug")),
};

// ─── Location Maps ──────────────────────────────────────────────────────────
export const locationMaps = {
    list: () => selectData("location_maps", { order: { column: "created_at" } }),
    get: (id) => selectData("location_maps", { filters: { id }, single: true }),
    create: (data) => first(insertData("location_maps", data)),
    update: (id, data) => first(updateData("location_maps", { id, values: stamp(data) })),
    delete: (id) => deleteData("location_maps", { id }),
};

// ─── Locations ──────────────────────────────────────────────────────────────
export const locations = {
    list: (params = {}) => {
        const filters = {};
        if (params.map_id) filters.map_id = params.map_id;
        if (params.active !== undefined) filters.is_active = params.active;
        return selectData("locations", { filters, order: { column: "name" } });
    },
    create: (data) => first(insertData("locations", data)),
    createMany: (rows) => insertData("locations", rows),
    update: (id, data) => first(updateData("locations", { id, values: stamp(data) })),
    delete: (id) => deleteData("locations", { id }),
    deleteByMap: (mapId) => deleteData("locations", { filters: { map_id: mapId } }),
};

// ─── Conversations ──────────────────────────────────────────────────────────
export const conversations = {
    list: (params = {}) => {
        const filters = {};
        if (params.status) filters.status = params.status;
        if (params.assigned_to) filters.assigned_to = params.assigned_to;
        if (params.unassigned) filters.$notnull = undefined; // handled below
        if (params.priority) filters.priority = params.priority;
        const opts = { filters, order: { column: "started_at", ascending: false } };
        // "unassigned" needs an IS NULL filter the generic map can't express;
        // fetch and filter client-side (lists are small) to keep the proxy tight.
        if (params.unassigned) {
            delete filters.$notnull;
            return selectData("chat_sessions", opts).then((r) => ({
                data: (r.data || []).filter((s) => !s.assigned_to), error: r.error,
            }));
        }
        return selectData("chat_sessions", opts);
    },
    get: (id) => selectData("chat_sessions", { filters: { id }, single: true }),
    getMessages: (sessionId) => selectData("chat_messages", {
        filters: { session_id: sessionId }, order: { column: "created_at" },
    }),
    update: (id, data) => first(updateData("chat_sessions", { id, values: data })),
    getNotes: (sessionId) => selectData("conversation_notes", {
        filters: { session_id: sessionId }, order: { column: "created_at", ascending: false },
    }),
    addNote: (data) => first(insertData("conversation_notes", data)),
    deleteNote: (id) => deleteData("conversation_notes", { id }),
    delete: async (id) => {
        // Cascade: delete messages + notes first, then the session.
        await deleteData("chat_messages", { filters: { session_id: id } });
        await deleteData("conversation_notes", { filters: { session_id: id } });
        return deleteData("chat_sessions", { id });
    },
    deleteMany: async (ids) => {
        for (let i = 0; i < ids.length; i += 50) {
            const batch = ids.slice(i, i + 50);
            await deleteData("chat_messages", { filters: { session_id: batch } });
            await deleteData("conversation_notes", { filters: { session_id: batch } });
            await deleteData("chat_sessions", { filters: { id: batch } });
        }
        return { error: null };
    },
    stats: () => rpc("get_conversation_stats"),
};

// ─── Leads ──────────────────────────────────────────────────────────────────
export const leads = {
    list: (params = {}) => {
        const filters = {};
        if (params.status) filters.status = params.status;
        if (params.assigned_to) filters.assigned_to = params.assigned_to;
        return selectData("chat_leads", { filters, order: { column: "created_at", ascending: false } });
    },
    get: (id) => selectData("chat_leads", { filters: { id }, single: true }),
    getSession: (sessionId) => selectData("chat_sessions", { filters: { id: sessionId }, single: true }),
    update: (id, data) => first(updateData("chat_leads", { id, values: stamp(data) })),
    setStatus: (id, status) => {
        const patch = { status };
        if (status === "contacted") patch.contacted_at = new Date().toISOString();
        return first(updateData("chat_leads", { id, values: patch }));
    },
    delete: (id) => deleteData("chat_leads", { id }),
    deleteMany: async (ids) => {
        for (let i = 0; i < ids.length; i += 50) {
            await deleteData("chat_leads", { filters: { id: ids.slice(i, i + 50) } });
        }
        return { error: null };
    },
};

// Conversation operations via edge function (unchanged).
export async function conversationOps(action, params = {}) {
    const account = getAccount();
    if (!account?.slug) return { error: "No account" };
    try {
        const res = await fetch(`${masterUrl}/functions/v1/conversation-ops?account=${account.slug}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${masterAnonKey}` },
            body: JSON.stringify({ action, ...params }),
        });
        return await res.json();
    } catch (err) {
        return { error: err.message };
    }
}

// ─── Analytics (all through the proxy; p_account_id injected server-side) ───
export const analytics = {
    summary: (days = 30) => rpc("get_analytics_summary", { p_days: days }),
    dailyMetrics: (days = 30) => rpc("get_daily_chat_metrics", { p_days: days }),
    intents: (days = 30) => rpc("get_top_intents", { p_days: days, p_limit: 15 }),
    responseStats: (days = 30) => rpc("get_response_stats", { p_days: days }),
    contentGaps: () => selectData("content_gaps", {
        filters: { status: "open" },
        order: { column: "occurrence_count", ascending: false }, limit: 20,
    }),
    recentSessions: (limit = 10) => selectData("chat_sessions", {
        order: { column: "started_at", ascending: false }, limit,
    }),
    topProducts: (days = 30) => rpc("get_top_chat_products", { p_days: days, p_limit: 10 }),
    sentimentBreakdown: (days = 30) => rpc("get_sentiment_breakdown", { p_days: days }),
    intentTags: (days = 30) => rpc("get_top_intent_tags", { p_days: days, p_limit: 15 }),
    sentimentTrend: (days = 30) => rpc("get_sentiment_trend", { p_days: days }),
    productMentions: (days = 30) => rpc("get_product_mentions", { p_days: days, p_limit: 15 }),
    productOpportunities: (days = 30) => rpc("get_product_opportunities", { p_days: days, p_limit: 10 }),
    lowMatchQueries: (days = 30) => rpc("get_low_match_queries", { p_days: days, p_limit: 10 }),
    conversationFunnel: (days = 30) => rpc("get_conversation_funnel", { p_days: days }),
    hourlyDistribution: (days = 30) => rpc("get_hourly_distribution", { p_days: days }),
    topPages: (days = 30) => rpc("get_top_pages", { p_days: days, p_limit: 10 }),
    teamPerformance: (days = 30) => rpc("get_team_performance", { p_days: days }),
    assignmentOverview: (days = 30) => rpc("get_assignment_overview", { p_days: days }),
};

// ─── Tenant Config ──────────────────────────────────────────────────────────
export const config = {
    get: () => selectData("tenant_config", { single: true }),
    // tenant_config is one-row-per-account; the proxy scopes by account, so an
    // empty filter map updates exactly this account's row.
    update: (data) => first(updateData("tenant_config", { filters: {}, values: stamp(data) })),
};

// ─── External Integrations (OAuth via /api/integrations — unchanged) ────────
export const integrations = {
    list: async () => {
        const { data: { session } } = await masterSupabase.auth.getSession();
        const res = await fetch("/api/integrations", { headers: { Authorization: `Bearer ${session?.access_token}` } });
        return await res.json();
    },
    update: async (provider, providerAccountId, metadata) => {
        const { data: { session } } = await masterSupabase.auth.getSession();
        const res = await fetch("/api/integrations", {
            method: "PATCH",
            headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ provider, provider_account_id: providerAccountId, provider_metadata: metadata }),
        });
        return await res.json();
    },
    disconnect: async (provider) => {
        const { data: { session } } = await masterSupabase.auth.getSession();
        const res = await fetch("/api/integrations", {
            method: "DELETE",
            headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ provider }),
        });
        return await res.json();
    },
    sync: async (provider, days = 30) => {
        const { data: { session } } = await masterSupabase.auth.getSession();
        const res = await fetch("/api/integrations", {
            method: "POST",
            headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ provider, days }),
        });
        return await res.json();
    },
    getOAuthUrl: async (provider) => {
        const { data: { session } } = await masterSupabase.auth.getSession();
        const res = await fetch(`/api/integrations/oauth/google?provider=${provider}`, {
            headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        return await res.json();
    },
};

// ─── Marketing Analytics (GA4 / GSC via proxy) ──────────────────────────────
export const marketingAnalytics = {
    ga4TrafficOverview: (days = 30) => rpc("get_ga4_traffic_overview", { p_days: days }),
    ga4SummaryRange: (start, end) => rpc("get_ga4_summary_range", { p_start: start, p_end: end }),
    ga4DailyRange: (start, end) => rpc("get_ga4_daily_range", { p_start: start, p_end: end }),
    ga4BreakdownRange: (start, end, dim, limit = 10) => rpc("get_ga4_breakdown_range", { p_start: start, p_end: end, p_dim: dim, p_limit: limit }),
    ga4SourcesRange: (start, end, limit = 10) => rpc("get_ga4_sources_range", { p_start: start, p_end: end, p_limit: limit }),
    gscSummaryRange: (start, end) => rpc("get_gsc_summary_range", { p_start: start, p_end: end }),
    gscDailyRange: (start, end) => rpc("get_gsc_daily_range", { p_start: start, p_end: end }),
    gscBreakdownRange: (start, end, dim, limit = 20) => rpc("get_gsc_breakdown_range", { p_start: start, p_end: end, p_dim: dim, p_limit: limit }),
    funnelRange: (start, end) => rpc("get_funnel_range", { p_start: start, p_end: end }),
    ga4DailyTraffic: (days = 30) => rpc("get_ga4_daily_traffic", { p_days: days }),
    ga4TopSources: (days = 30) => rpc("get_ga4_top_sources", { p_days: days, p_limit: 10 }),
    ga4TopLandingPages: (days = 30) => rpc("get_ga4_top_landing_pages", { p_days: days, p_limit: 10 }),
    ga4DeviceBreakdown: (days = 30) => rpc("get_ga4_device_breakdown", { p_days: days }),
    gscOverview: (days = 30) => rpc("get_gsc_overview", { p_days: days }),
    gscDailyTrend: (days = 30) => rpc("get_gsc_daily_trend", { p_days: days }),
    gscTopQueries: (days = 30) => rpc("get_gsc_top_queries", { p_days: days, p_limit: 20 }),
    gscTopPages: (days = 30) => rpc("get_gsc_top_pages", { p_days: days, p_limit: 10 }),
    blendedDailyOverview: (days = 30) => rpc("get_blended_daily_overview", { p_days: days }),
};

// ─── Training Layer ─────────────────────────────────────────────────────────
export const training = {
    qaList: () => selectData("training_qa_pairs", { order: { column: "created_at", ascending: false } }),
    qaCreate: (data) => first(insertData("training_qa_pairs", data)),
    qaUpdate: (id, data) => first(updateData("training_qa_pairs", { id, values: stamp(data) })),
    qaDelete: (id) => deleteData("training_qa_pairs", { id }),

    correctionsList: (status) => selectData("training_corrections", {
        filters: status ? { status } : {},
        order: { column: "created_at", ascending: false }, limit: 100,
    }),
    correctionUpdate: (id, data) => first(updateData("training_corrections", { id, values: stamp(data) })),

    // Build the review queue from real signals: thumbs-down messages and open
    // content gaps not already queued. Same logic as before, now via the proxy.
    buildQueue: async () => {
        if (!getAccountId()) return { added: 0 };
        let added = 0;

        // 1) Negative-feedback assistant messages
        const { data: allBad } = await selectData("chat_messages", {
            columns: "id, session_id, content, created_at, role, feedback_rating",
            filters: { role: "assistant" },
            order: { column: "created_at", ascending: false }, limit: 200,
        });
        const badMsgs = (allBad || []).filter((m) => (m.feedback_rating ?? 0) < 0).slice(0, 30);
        for (const m of badMsgs) {
            const { data: exists } = await selectData("training_corrections", {
                columns: "id", filters: { message_id: m.id }, limit: 1,
            });
            if (exists?.length) continue;
            const { data: sessMsgs } = await selectData("chat_messages", {
                columns: "content, created_at, role",
                filters: { session_id: m.session_id, role: "user" },
                order: { column: "created_at", ascending: false }, limit: 20,
            });
            const userMsg = (sessMsgs || []).find((u) => u.created_at < m.created_at);
            const { error } = await insertData("training_corrections", {
                session_id: m.session_id, message_id: m.id,
                visitor_question: userMsg?.content || "(question unavailable)",
                original_answer: m.content, flag_reason: "Visitor gave a thumbs-down",
            });
            if (!error) added++;
        }

        // 2) Open content gaps
        const { data: gaps } = await selectData("content_gaps", {
            columns: "id, query_text, occurrence_count",
            filters: { status: "open" },
            order: { column: "occurrence_count", ascending: false }, limit: 15,
        });
        for (const g of gaps || []) {
            const { data: exists } = await selectData("training_corrections", {
                columns: "id", filters: { flag_reason: `content_gap:${g.id}` }, limit: 1,
            });
            if (exists?.length) continue;
            const { error } = await insertData("training_corrections", {
                visitor_question: g.query_text,
                original_answer: "(no confident answer — asked " + (g.occurrence_count || 1) + "×)",
                flag_reason: `content_gap:${g.id}`,
            });
            if (!error) added++;
        }
        return { added };
    },

    guardrailsList: () => selectData("training_guardrails", { order: [{ column: "sort_order" }, { column: "created_at" }] }),
    guardrailCreate: (data) => first(insertData("training_guardrails", data)),
    guardrailUpdate: (id, data) => first(updateData("training_guardrails", { id, values: stamp(data) })),
    guardrailDelete: (id) => deleteData("training_guardrails", { id }),

    sourcesList: () => selectData("knowledge_sources", { order: { column: "created_at", ascending: false } }),
    sourceCreate: (data) => first(insertData("knowledge_sources", data)),
    sourceUpdate: (id, data) => first(updateData("knowledge_sources", { id, values: stamp(data) })),
    sourceDelete: (id) => deleteData("knowledge_sources", { id }),
};

// Report generation via edge function (unchanged).
export async function reportOps(action, params = {}) {
    const account = getAccount();
    if (!account?.slug) return { error: "No account" };
    try {
        const res = await fetch(`${masterUrl}/functions/v1/report-ops?account=${account.slug}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${masterAnonKey}` },
            body: JSON.stringify({ action, ...params }),
        });
        return await res.json();
    } catch (err) {
        return { error: err.message };
    }
}

// Server-side training operations via edge function (unchanged).
export async function trainingOps(action, params = {}) {
    const account = getAccount();
    if (!account?.slug) return { error: "No account" };
    try {
        const res = await fetch(`${masterUrl}/functions/v1/training-ops?account=${account.slug}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${masterAnonKey}` },
            body: JSON.stringify({ action, ...params }),
        });
        return await res.json();
    } catch (err) {
        return { error: err.message };
    }
}
